if (!customElements.get('kp-fragrance-explorer')) {
  const requests = new Map();
  const census = async (url) => {
    const key = url.href;
    if (requests.has(key)) return requests.get(key);
    const pending = (async () => {
      const read = async (page) => {
        const request = new URL(url);
        request.searchParams.set('section_id', 'kp-fragrance-brand-counts');
        request.searchParams.set('page', page);
        const response = await fetch(request, { signal: AbortSignal.timeout(20000) });
        if (!response.ok) throw new Error('Brand counts unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const data = JSON.parse(doc.querySelector('[data-brand-census]')?.textContent || 'null');
        if (!data || data.page !== page || !Array.isArray(data.vendors)) throw new Error('Invalid brand counts');
        return data;
      };
      const first = await read(1);
      const vendors = [...first.vendors];
      // Bound requests to three at a time; no full product descriptions or images are downloaded.
      for (let page = 2; page <= first.pages; page += 3) {
        const pages = await Promise.all(Array.from({ length: Math.min(3, first.pages - page + 1) }, (_, i) => read(page + i)));
        for (const data of pages) {
          if (data.total !== first.total) throw new Error('Collection changed during count');
          vendors.push(...data.vendors);
        }
      }
      if (vendors.length !== first.total) throw new Error('Incomplete brand counts');
      const counts = new Map();
      vendors.forEach(name => { if (name) counts.set(name, (counts.get(name) || 0) + 1); });
      return [...counts];
    })();
    requests.set(key, pending);
    pending.catch(() => requests.delete(key));
    setTimeout(() => requests.delete(key), 120000);
    return pending;
  };
  customElements.define('kp-fragrance-explorer', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.current = new URL(location.href);
      if ([...this.current.searchParams.keys()].some(key => key.startsWith('filter.')) && !this.querySelector('[data-reset-filters]')) {
        const reset = document.createElement('a');
        reset.href = this.current.pathname + '#coll-grid';
        reset.textContent = 'Reset filters';
        reset.dataset.resetFilters = '';
        this.querySelector('.kp-fexplorer__availability')?.append(reset);
      }
      this.querySelector('[data-brand-search]')?.addEventListener('input', () => this.search());
      const countUrl = new URL(this.current);
      countUrl.searchParams.delete('filter.p.vendor');
      countUrl.searchParams.delete('page');
      countUrl.searchParams.delete('sort_by');
      countUrl.searchParams.delete('section_id');
      countUrl.hash = '';
      countUrl.searchParams.sort();
      // BRAND CENSUS ON DEMAND (26 Oct 2026). Counting every brand fetches ~14 collection pages, and it
      // ran on every /collections/fragrances load although its only output is the folded "Find a brand
      // A-Z" list. The server already renders the brand chips and a first A-Z list, so the census now
      // starts when that list opens or the brand search is used, and is kept for 30 minutes per session.
      // The chips above it stay as the server rendered them, so nothing shifts when the list opens.
      const all = this.querySelector('.kp-fexplorer__all');
      const start = () => {
        if (this.censusStarted || !this.querySelector('.kp-fexplorer__directory')) return;
        this.censusStarted = true;
        this.runCensus(countUrl);
      };
      if (all?.open) start();
      all?.addEventListener('toggle', () => { if (all.open) start(); });
      this.querySelector('[data-brand-search]')?.addEventListener('focus', start, { once: true });
    }
    runCensus(countUrl) {
      const cacheKey = 'kpBrandCensus:' + countUrl.href;
      let cached = null;
      try {
        const raw = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
        if (raw && Date.now() - raw.t < 1800000 && Array.isArray(raw.counts)) cached = raw.counts;
      } catch (_) {}
      (cached ? Promise.resolve(cached) : census(countUrl).then(counts => {
        try { sessionStorage.setItem(cacheKey, JSON.stringify({ t: Date.now(), counts })); } catch (_) {}
        return counts;
      })).then(counts => {
          if (!this.isConnected) return;
          const alphabetical = counts.filter(([, count]) => count > 0).sort(([a], [b]) => a.localeCompare(b));
          this.querySelector('.kp-fexplorer__directory').replaceChildren(...alphabetical.map(entry => this.link(entry, 'brandName')));
          this.querySelector('[data-brand-total]').textContent = `(${alphabetical.length})`;
          this.querySelector('[data-brand-status]').hidden = true;
          this.search();
        }).catch(() => {
          if (!this.isConnected) return;
          this.querySelector('[data-brand-status]').textContent = 'Showing verified brands. The complete brand list could not be loaded; refresh to try again.';
        });
    }
    link([name, count], attribute) {
      const link = document.createElement('a');
      link.dataset[attribute] = name;
      const url = new URL(this.current);
      const selected = url.searchParams.getAll('filter.p.vendor');
      const active = selected.includes(name);
      url.searchParams.delete('filter.p.vendor');
      (active ? selected.filter(value => value !== name) : [...selected, name]).forEach(value => url.searchParams.append('filter.p.vendor', value));
      url.searchParams.delete('page');
      // Brands with their own collection page link there (indexable) instead of a ?filter. URL.
      const handle = name.replace(/ Perfumes$/, '').toLowerCase().replace(/['’]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      const pages = this.dataset.brandPages || '';
      const page = !active && ['perfume', 'aftershave'].map(s => `${handle}-${s}`).find(h => pages.includes(`,${h},`));
      if (page) {
        link.href = `${this.dataset.collectionsUrl || '/collections'}/${page}`;
        link.append(document.createTextNode(name + ' '));
        const number = document.createElement('span');
        number.textContent = count;
        link.append(number);
        return link;
      }
      url.hash = 'coll-grid';
      link.href = url.href;
      link.append(document.createTextNode(name + ' '));
      const number = document.createElement('span');
      number.textContent = count;
      link.append(number);
      if (active) {
        link.setAttribute('aria-current', 'true');
        const remove = document.createElement('span');
        remove.textContent = '×';
        remove.setAttribute('aria-label', 'Remove filter');
        link.append(remove);
      }
      return link;
    }
    search() {
      const query = this.querySelector('[data-brand-search]').value.trim().toLocaleLowerCase();
      let matches = 0;
      this.querySelectorAll('[data-brand-name]').forEach(link => {
        link.hidden = !link.dataset.brandName.toLocaleLowerCase().includes(query);
        if (!link.hidden) matches++;
      });
      this.querySelector('[data-brand-empty]').hidden = matches > 0;
    }
  });
}
